import { tt } from '../i18n';
import { useKeyboard } from './useKeyboard';
import { TextPrompt } from './Prompts';
import { useState } from 'react';
import type { GuestLookupResponse } from '@dastarhan/contracts';
import { formatMoney, formatPhoneKz } from '@dastarhan/core';
import { api } from '../api';

/** Телефон — и есть карта гостя. Один запрос: найти или завести,
 *  вернуть процент. Требует связи — как elevate. */
export function GuestDialog({ token, onPick, onRepeat, onCancel }: {
  token: string;
  onPick: (g: GuestLookupResponse) => void;
  /** привязать гостя И добавить позиции его последнего чека */
  onRepeat: (g: GuestLookupResponse) => void;
  onCancel: () => void;
}) {
  const [noteOpen, setNoteOpen] = useState(false);
  const [phone, setPhone] = useState('');
  const kb = useKeyboard(phone, setPhone, 'number');
  // Постоянные гости звонят постоянно: последние 5 номеров под рукой
  type Recent = { phone: string; name?: string };
  const recent: Recent[] = (() => {
    try {
      const raw = JSON.parse(localStorage.getItem('dastarhan.recentGuests')
        ?? localStorage.getItem('dastarhan.recentPhones') ?? '[]') as (string | Recent)[];
      // миграция со строкового формата на пары {phone, name}
      return raw.map((x) => (typeof x === 'string' ? { phone: x } : x));
    } catch { return []; }
  })();
  const remember = (p: string, nm?: string | null) => {
    const next = [{ phone: p, ...(nm ? { name: nm } : {}) },
      ...recent.filter((x) => x.phone !== p)].slice(0, 5);
    localStorage.setItem('dastarhan.recentGuests', JSON.stringify(next));
  };
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [found, setFound] = useState<GuestLookupResponse | null>(null);

  const lookup = async () => {
    setBusy(true); setErr('');
    try {
      const g = await api.guestLookup(token, {
        phone,
        ...(name.trim() ? { name: name.trim() } : {}),
      });
      setFound(g);
      remember(phone.trim(), g.name);
    } catch {
      setErr('Телефон не похож на казахстанский или нет связи.');
    } finally { setBusy(false); }
  };

  return (
    <div className="dialog-backdrop" role="dialog" aria-modal="true" aria-label="Гость">
      <div className="dialog" style={{ maxWidth: 360, width: '100%' }}>
        <span className="eyebrow">Гость по телефону</span>

        {!found && (
          <>
            <h1>Какой номер?</h1>
            <input className="field" style={{ width: '100%' }} inputMode="tel"
            {...kb.props} autoFocus
              placeholder="+7 701 234-56-78" value={phone}
              onChange={(e) => setPhone(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && phone.trim() && lookup()} />
            {recent.length > 0 && !phone && (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {recent.map((r) => (
                  <button key={r.phone} className="btn small" title={r.phone}
                    onClick={() => setPhone(r.phone)}>
                    {r.name ?? r.phone}
                  </button>
                ))}
              </div>
            )}
            <input className="field" style={{ width: '100%' }}
              placeholder="Имя (необязательно)" value={name}
              onChange={(e) => setName(e.target.value)} />
            <p className="error">{err}</p>
            <div className="mod-actions" style={{ width: '100%' }}>
              <button className="btn ghost" onClick={onCancel}>Отмена</button>
              <button className="btn accent" disabled={busy || phone.trim().length < 5} onClick={lookup}>
                {busy ? '…' : tt('find')}
              </button>
            </div>
          </>
        )}

        {found && (
          <>
            <h1>{found.name ?? formatPhoneKz(found.phone)}</h1>
            <p className="hint" style={{ margin: 0 }}>
              {found.visits > 0
                ? <>Визитов: {found.visits} · куплено на <b className="money">{formatMoney(found.totalSpent)}</b></>
                : 'Первый визит — добро пожаловать!'}
            </p>
            {found.favorites.length > 0 && (
              <p className="hint" style={{ margin: 0 }}>
                Любимое: {found.favorites.map((f) => `${f.name} ×${f.qty}`).join(', ')}
              </p>
            )}
            {found.recentOrders.length > 0 && (
              <div className="guest-history">
                {found.recentOrders.map((o, i) => (
                  <div key={i}>
                    <span>{new Date(o.at).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' })}</span>
                    <b className="money">{formatMoney(o.total)}</b>
                  </div>
                ))}
              </div>
            )}
            <p className="hint" style={{ margin: 0 }}>
              {found.percent > 0
                ? <>Лояльность: <b style={{ color: 'var(--ok)' }}>−{found.percent}%</b> на этот чек</>
                : 'Скидка лояльности пока не настроена'}
            </p>
            {found.note && (
              <p className="guest-note">📝 {found.note}</p>
            )}
            <button className="btn small" style={{ alignSelf: 'flex-start' }}
              onClick={() => setNoteOpen(true)}>
              {found.note ? tt('note') + ' ✎' : '+ ' + tt('note')}
            </button>
            {noteOpen && (
              <TextPrompt
                title="Заметка о госте"
                hint="Аллергии, предпочтения — увидит любой, кто откроет карточку"
                initial={found.note ?? ''}
                maxLength={200}
                allowEmpty
                onCancel={() => setNoteOpen(false)}
                onDone={(v) => {
                  setNoteOpen(false);
                  void api.guestPatch(token, found.guestId, { note: v || null })
                    .then(() => setFound({ ...found, note: v || null }))
                    .catch(() => alert('Не сохранилось.'));
                }} />
            )}
            {/* Решения — одним рядом внизу, как во всех диалогах кассы */}
            <div className="mod-actions" style={{ width: '100%' }}>
              <button className="btn ghost" onClick={() => setFound(null)}>← Другой номер</button>
              {found.lastOrderItems.length > 0 && (
                <button className="btn" onClick={() => onRepeat(found)}>
                  Повторить прошлый заказ ({found.lastOrderItems.length} поз.)
                </button>
              )}
              <button className="btn accent" onClick={() => onPick(found)}>Привязать к чеку</button>
            </div>
          </>
        )}
      </div>
      {kb.node}
    </div>
  );
}
